/**
 * lib/documents/global-document-type-form.ts — wspólne mapery formularza typu
 * globalnego dokumentu (panel ITE).
 *
 * Jedno źródło prawdy dla dwóch callerów:
 *  - `AdminGlobalDocumentTypes.tsx` — pełna strona „Globalne typy dokumentów",
 *  - `DocumentTypeEditorModal.tsx` — edytor typu z zakładkami (wiersz typu w Kartotekach).
 *
 * `toFormValues` — rekord backendu → wartości formularza; `buildCreatePayload` /
 * `buildUpdatePayload` — wartości formularza → body żądania (update pisze tylko delty);
 * `handleHrAbsenceSourceErrorCode` — wspólne mapowanie błędów flagi „wniosek o nieobecność".
 */

import type { AdminGlobalDocumentType, DocumentClass } from '@saas/shared';
import type { TFunction } from 'i18next';

import type { GlobalDocumentTypeFormValues } from '@/components/documents/GlobalDocumentTypeFormModal';
import type { ToastContextValue } from '@/contexts/ToastContext';

/**
 * Mapuje kody walidacji flagi „wniosek o nieobecność" (typ domyślny ITE) na toast ostrzegawczy —
 * wspólne dla `onError` mutacji create/update na stronie typów globalnych i w edytorze typu
 * (Kartoteki). Zwraca `true`, gdy kod został obsłużony (wołający przerywa przed ogólnym błędem).
 */
export function handleHrAbsenceSourceErrorCode(
  code: string | null,
  toast: ToastContextValue,
  t: TFunction,
): boolean {
  if (code === 'HR_ABSENCE_SOURCE_ALREADY_SET') {
    toast.show({
      variant: 'warning',
      title: t('adminDocumentTypes.toast.hrAbsenceSourceAlreadySet'),
    });
    return true;
  }
  if (code === 'HR_ABSENCE_SOURCE_CLASS_NOT_ALLOWED') {
    toast.show({
      variant: 'warning',
      title: t('adminDocumentTypes.toast.hrAbsenceSourceClassNotAllowed'),
    });
    return true;
  }
  return false;
}

/** Rekord typu globalnego (backend) → wartości formularza (prefill edycji). */
export function toFormValues(type: AdminGlobalDocumentType): GlobalDocumentTypeFormValues {
  return {
    code: type.code,
    name: type.name,
    description: type.description ?? '',
    numberingFormat: type.numberingFormat ?? '',
    numberingBaseField: type.numberingBaseField ?? 'document_date',
    numberingReset: type.numberingReset,
    sortOrder:
      type.sortOrder !== null && type.sortOrder !== undefined ? String(type.sortOrder) : '',
    formViewSlug: type.formViewSlug,
    defaultProcessTemplateId: type.defaultProcessTemplateId,
    isHrAbsenceSource: type.isHrAbsenceSource,
  };
}

/** Wartości formularza → body POST (create) — klasa z kontekstu strony. */
export function buildCreatePayload(
  documentClass: DocumentClass,
  values: GlobalDocumentTypeFormValues,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    class: documentClass,
    code: values.code,
    name: values.name,
  };
  if (values.description.trim()) body.description = values.description.trim();
  if (values.numberingFormat.trim()) body.numberingFormat = values.numberingFormat.trim();
  if (values.numberingBaseField) body.numberingBaseField = values.numberingBaseField;
  body.numberingReset = values.numberingReset;
  if (values.sortOrder.trim() !== '') body.sortOrder = parseInt(values.sortOrder, 10);
  if (values.formViewSlug) body.formViewSlug = values.formViewSlug;
  if (values.defaultProcessTemplateId)
    body.defaultProcessTemplateId = values.defaultProcessTemplateId;
  if (values.isHrAbsenceSource) body.isHrAbsenceSource = true;
  return body;
}

/** Wartości formularza → body PUT (update) — tylko pola zmienione względem oryginału. */
export function buildUpdatePayload(
  values: GlobalDocumentTypeFormValues,
  original: AdminGlobalDocumentType,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (values.code !== original.code) payload.code = values.code;
  if (values.name !== original.name) payload.name = values.name;
  const desc = values.description.trim() || null;
  if (desc !== original.description) payload.description = desc;
  const fmt = values.numberingFormat.trim() || null;
  if (fmt !== original.numberingFormat) payload.numberingFormat = fmt;
  const baseField = values.numberingBaseField || 'document_date';
  const origBaseField = original.numberingBaseField ?? 'document_date';
  if (baseField !== origBaseField) payload.numberingBaseField = baseField;
  if (values.numberingReset !== original.numberingReset)
    payload.numberingReset = values.numberingReset;
  const sort = values.sortOrder.trim() !== '' ? parseInt(values.sortOrder, 10) : null;
  if (sort !== original.sortOrder) payload.sortOrder = sort;
  if (values.formViewSlug !== original.formViewSlug) payload.formViewSlug = values.formViewSlug;
  if (values.defaultProcessTemplateId !== original.defaultProcessTemplateId)
    payload.defaultProcessTemplateId = values.defaultProcessTemplateId;
  if (values.isHrAbsenceSource !== original.isHrAbsenceSource)
    payload.isHrAbsenceSource = values.isHrAbsenceSource;
  return payload;
}
